import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
    Map,
    Gift,
    Trophy,
    Sparkles,
    Download,
    UserPlus,
    MapPin,
    Coffee,
    ArrowRight,
    QrCode
} from 'lucide-react';
import { staggerContainer, fadeInUp } from '@/hooks/use-scroll-animation';
import { Button } from '@/components/ui/button';
import { BenefitsSection } from './benefits';

// Mapping for the 8-step Gamified Timeline
const stepsIcons = {
    1: Download,   // Baixar
    2: UserPlus,   // Cadastrar
    3: Map,        // Explorar
    4: MapPin,     // Encontrar
    5: Coffee,     // Saboreie (NEW)
    6: QrCode,     // Fidelize (MOVED, NEW ICON)
    7: Gift,       // Resgatar (MOVED)
    8: Trophy      // Ranking (MOVED)
};

export function ConsumerSection() {
    const { t } = useTranslation();

    return (
        <section id="how-it-works" className="py-20 md:py-32 bg-background relative overflow-hidden w-full">
            {/* Background Decor */}
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />

            <div className="container mx-auto px-4 relative z-10 w-full">

                {/* 1. Header Section */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={fadeInUp}
                    className="text-center mb-16 md:mb-24 w-full"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#6F4E37]/10 text-[#6F4E37] text-sm font-medium mb-6"
                    >
                        <Sparkles size={14} />
                        <span className="uppercase tracking-wider text-xs font-bold">{t('consumer.title')}</span>
                    </motion.div>

                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold mb-6 text-[#2D1810]">
                        {t('consumer.subtitle')}
                    </h2>
                </motion.div>

                {/* 2. Timeline Gamificada (8 Passos) */}
                <div className="mb-32 relative">
                    {/* Linha conectora (Desktop Only) */}
                    <div className="hidden lg:block absolute top-[40px] left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#6F4E37]/20 to-transparent z-0" />

                    <motion.div
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-y-12 gap-x-4 relative z-10"
                    >
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((step, index) => {
                            const Icon = stepsIcons[step as keyof typeof stepsIcons];
                            return (
                                <motion.div
                                    key={step}
                                    variants={fadeInUp}
                                    className="flex flex-col items-center text-center group relative"
                                >
                                    {/* Step Connector Number (Mobile Only) */}
                                    {/* <div className="lg:hidden absolute top-0 right-4 text-[50px] font-display font-bold text-[#6F4E37]/5 -z-10 leading-none select-none">
                                        {step}
                                    </div> */}

                                    {/* Icon Circle */}
                                    <div className="w-20 h-20 rounded-full bg-[#FDF8F3] border-4 border-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex items-center justify-center mb-6 relative z-10 transition-all duration-500 group-hover:scale-110 group-hover:shadow-[0_20px_40px_rgb(111,78,55,0.15)] group-hover:border-[#6F4E37]/10">
                                        <div className="absolute inset-0 rounded-full bg-[#6F4E37] opacity-0 group-hover:opacity-5 transition-opacity duration-500 scale-0 group-hover:scale-100" />
                                        <Icon className="text-[#6F4E37] h-8 w-8 transition-transform duration-500 group-hover:rotate-6" strokeWidth={1.5} />

                                        {/* Step Badge */}
                                        <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#6F4E37] text-white text-xs font-bold flex items-center justify-center border-2 border-white shadow-sm">
                                            {step}
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <h3 className="font-display font-bold text-lg mb-2 text-[#2D1810] leading-tight px-1">
                                        {t(`consumer.steps.${step}.title`)}
                                    </h3>
                                    <p className="text-sm text-[#6B5B4F] leading-relaxed max-w-[140px] px-1">
                                        {t(`consumer.steps.${step}.desc`)}
                                    </p>

                                    {/* Connector Arrow (Mobile Grid) */}
                                    {index < 7 && (index % 2 === 0) && (
                                        <div className="block md:hidden absolute top-8 -right-6 text-[#6F4E37]/20 z-0">
                                            <ArrowRight size={24} />
                                        </div>
                                    )}
                                    {/* Connector Arrow (Tablet Grid - 4 cols) */}
                                    {index < 7 && ((index + 1) % 4 !== 0) && (
                                        <div className="hidden md:block xl:hidden absolute top-8 -right-6 text-[#6F4E37]/20 z-0">
                                            <ArrowRight size={24} />
                                        </div>
                                    )}
                                </motion.div>
                            );
                        })}
                    </motion.div>
                </div>

                {/* 3. Benefícios (NEW SECTION) */}
                <motion.div
                    id="features"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    className="relative scroll-mt-24"
                >
                    {/* Section Title */}
                    <div className="text-center mb-12">
                        <h3 className="text-3xl md:text-5xl font-display font-bold text-[#2D1810] mb-4">
                            {t('consumer.benefits.title')}
                        </h3>
                        <p className="text-lg md:text-xl text-[#6B5B4F] px-4 md:px-0 mx-auto leading-relaxed">
                            {t('consumer.benefits.subtitle')}
                        </p>
                    </div>

                    <BenefitsSection />

                </motion.div>

                {/* 4. Bottom CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 }}
                    className="mt-20 text-center"
                >
                    <Button
                        size="lg"
                        className="h-14 px-10 rounded-full bg-[#6F4E37] hover:bg-[#5D4230] text-white text-lg shadow-xl shadow-[#6F4E37]/20 hover:shadow-[#6F4E37]/40 transition-all duration-300"
                    >
                        <Download className="mr-2 h-5 w-5" />
                        {t('hero.cta.primary')}
                    </Button>

                    {/* Market Stat */}
                    <p className="mt-8 text-sm text-[#6B5B4F] italic opacity-80">
                        "{t('consumer.marketStat.text')}" — {t('consumer.marketStat.source')}
                    </p>
                </motion.div>
            </div>
        </section>
    );
}
